/**
 * Menu-flow navigation. The game screen and the lobby room are NOT screens here: they are derived
 * from `gameStore.view` and `lobbyStore.room` (see App.tsx), so the net layer drives them.
 */
import { createStore, useStore } from 'zustand';

export type Screen =
  | 'menu'
  | 'singleplayer'
  | 'scenarios'
  | 'multiplayer'
  | 'archive'
  | 'manual'
  | 'settings';

interface NavState {
  screen: Screen;
  go(screen: Screen): void;
}

/**
 * Room code of an invite link (`/?join=ABCDE`), read once at start-up. The link opens the
 * multiplayer screen, which joins the room as soon as the commander is online.
 */
export const inviteCode: string | null = (() => {
  try {
    const code = new URLSearchParams(globalThis.location?.search ?? '').get('join');
    return code && /^[A-Za-z]{5}$/.test(code) ? code.toUpperCase() : null;
  } catch {
    return null;
  }
})();

/** The invite link of a room, for this server. */
export const inviteLink = (code: string): string => `${globalThis.location.origin}/?join=${code}`;

export const navStore = createStore<NavState>()((set) => ({
  screen: inviteCode ? 'multiplayer' : 'menu',
  go: (screen) => set({ screen }),
}));

export function useNav<T>(selector: (s: NavState) => T): T {
  return useStore(navStore, selector);
}
